import React, { useState, useEffect, useRef, useContext } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "../../contexts/UserContext";
import { useSubscription } from "../../hooks/useSubscription";
import { UNIFIED_MENU } from "../../config/subscriptionConfig";
import {
  LayoutDashboard,
  Award,
  Users,
  Send,
  FileText,
  GraduationCap,
  Bell,
  User,
  Settings,
  LogOut,
  AlarmClock,
  HardHat,
  Package,
  Truck,
  Clock,
  Calendar,
  Shield,
  ClipboardList,
  FolderKanban,
  ChevronDown,
  Phone,
  CreditCard,
  QrCode,
  ArrowLeftRight,
  MessageCircle,
  CalendarCheck,
  RefreshCw,
} from "lucide-react";
import logoSygnet from "../../assets/V1_sygnet.png";
import api from "../../services/apiService";

// Mapowanie nazw ikon na komponenty
const ICON_MAP = {
  LayoutDashboard,
  Award,
  Users,
  Send,
  FileText,
  GraduationCap,
  AlarmClock,
  HardHat,
  Package,
  Truck,
  Clock,
  Calendar,
  Shield,
  ClipboardList,
  Bell,
  FolderKanban,
  CalendarCheck,
  MessageCircle,
};

const UnifiedSidebar = ({ collapsed, toggleCollapse, setSidebarCollapsed, onOpenPhoneBook, viewMode, setViewMode }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const [logoPhase, setLogoPhase] = useState(0);
  const [ticketNotifications, setTicketNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const userMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useContext(UserContext);

  const { loading: subscriptionLoading, filterMenu, hasSubscription, hasAnySubscription } = useSubscription();

  // Sprawdź czy user jest adminem (może przełączać widok)
  const isAdmin = ['admin', 'adminves', 'superuser'].includes(user?.role);

  // Logo animation
  useEffect(() => {
    const timers = [];
    timers.push(setTimeout(() => setLogoPhase(1), 2000));
    timers.push(setTimeout(() => setLogoPhase(2), 4000));
    return () => timers.forEach(t => clearTimeout(t));
  }, []);

  // Filter menu based on subscriptions and user role
  const baseMenuItems = filterMenu(UNIFIED_MENU);

  // Dodatkowe filtrowanie dla adminów w zależności od viewMode
  const visibleMenuItems = isAdmin
    ? baseMenuItems.filter(item => {
        if (item.type === 'separator') return true;
        if (!item.roles || item.roles.length === 0) return true;

        if (viewMode === 'admin') {
          // W trybie admin ukryj pozycje "my-*" (typowo userowe)
          if (item.id.startsWith('my-')) return false;
          // Pokazuj tylko pozycje które mają role admina
          return item.roles.some(r => ['admin', 'adminves', 'superuser'].includes(r));
        } else {
          // W trybie user pokazuj tylko pozycje "my-*" lub te które mają role usera ale nie są typowo adminowe
          const hasUserRole = item.roles.some(r => ['user', 'userves'].includes(r));
          const isUserItem = item.id.startsWith('my-');
          return hasUserRole || isUserItem;
        }
      })
    : baseMenuItems;

  // Load ticket notifications
  useEffect(() => {
    const loadTicketNotifications = async () => {
      if (!user?.id) return;
      try {
        const response = await api.get('/support/tickets/unread_notifications/');
        setTicketNotifications(response.data.notifications || []);
        setNotificationCount(response.data.count || 0);
      } catch (error) {
        console.error('Error loading notifications:', error);
      }
    };
    loadTicketNotifications();
    const interval = setInterval(loadTicketNotifications, 60000);
    return () => clearInterval(interval);
  }, [user?.id]);

  // Handle clicks outside menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to log out?")) {
      if (logout) logout();
      navigate("/login", { replace: true });
    }
  };

  const isActive = (path) => {
    if (!path) return false;
    const pathBase = path.split('/')[1];
    const currentBase = location.pathname.split('/')[1];
    return currentBase === pathBase || location.pathname.startsWith(path);
  };

  const handleLinkClick = (e, targetPath) => {
    if (location.pathname === targetPath) {
      e.preventDefault();
      toggleCollapse?.();
    }
  };

  const effectivelyCollapsed = collapsed && !isHovering;

  const renderIcon = (iconName, isItemActive) => {
    const IconComponent = ICON_MAP[iconName];
    if (!IconComponent) return null;
    return (
      <IconComponent
        size={18}
        className={`flex-shrink-0 ${isItemActive ? "text-white" : "text-slate-600"}`}
      />
    );
  };

  const toggleSubmenu = (itemId) => {
    setOpenMenus(prev => prev[itemId] ? {} : { [itemId]: true });
  };

  const renderMenuItem = (item) => {
    // Handle separator
    if (item.type === 'separator') {
      if (effectivelyCollapsed) return null;
      return (
        <div key={item.id} className="pt-4 pb-2">
          <span className="px-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
            {item.label}
          </span>
        </div>
      );
    }

    const itemIsActive = isActive(item.path);
    const hasSubmenu = item.submenu && item.submenu.length > 0;
    const isSubmenuOpen = openMenus[item.id] || itemIsActive;

    // Filter submenu items
    const filteredSubmenu = item.submenu?.filter(subItem => {
      // Check vestasOnly
      if (subItem.vestasOnly && user?.company_name !== 'Vestas') return false;
      // Check excludeIfCoordinator - wyklucz dla coordinatorów (pokazujemy im wersję adminową)
      if (subItem.excludeIfCoordinator && user?.isCoordinator) return false;

      // Dla adminów filtruj submenu na podstawie viewMode
      if (isAdmin) {
        if (viewMode === 'admin') {
          // W trybie admin ukryj pozycje "my-*" w submenu
          if (subItem.id.startsWith('my-')) return false;
          // Sprawdź role tylko jeśli są zdefiniowane
          if (subItem.roles && subItem.roles.length > 0) {
            if (!subItem.roles.some(r => ['admin', 'adminves', 'superuser'].includes(r))) {
              // Chyba że ma allowCoordinator (wtedy to pozycja admina)
              if (!subItem.allowCoordinator) return false;
            }
          }
        } else {
          // W trybie user pokazuj tylko pozycje z rolami usera lub "my-*"
          const hasUserRole = !subItem.roles || subItem.roles.length === 0 || subItem.roles.some(r => ['user', 'userves'].includes(r));
          const isUserItem = subItem.id.startsWith('my-');
          if (!hasUserRole && !isUserItem) return false;
        }
      } else {
        // Standardowa logika dla nie-adminów
        if (subItem.roles && subItem.roles.length > 0 && !subItem.roles.includes(user?.role)) {
          // Check coordinator access for submenu
          if (!(subItem.allowCoordinator && user?.isCoordinator)) return false;
        }
      }

      // Check submenu subscriptions
      if (subItem.requiredSubscriptions && subItem.requiredSubscriptions.length > 0) {
        return subItem.requiredSubscriptions.some(subType => hasSubscription(subType));
      }
      return true;
    });

    return (
      <div key={item.id}>
        <div className="flex items-center">
          <Link
            to={item.path}
            onClick={(e) => {
              handleLinkClick(e, item.path);
              if (hasSubmenu && !openMenus[item.id]) {
                setOpenMenus({ [item.id]: true });
              }
            }}
            className={`flex-1 flex items-center gap-2 px-2 py-2 rounded-xl transition-all ${
              itemIsActive
                ? "bg-brand-500 text-white shadow-lg shadow-brand-500/20"
                : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            } ${effectivelyCollapsed ? "justify-center" : ""}`}
            title={effectivelyCollapsed ? item.label : undefined}
          >
            {renderIcon(item.icon, itemIsActive)}
            {!effectivelyCollapsed && (
              <span className="text-sm font-bold truncate">{item.label}</span>
            )}
          </Link>
          {hasSubmenu && filteredSubmenu?.length > 0 && !effectivelyCollapsed && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleSubmenu(item.id);
              }}
              className="p-2 text-slate-400 hover:text-slate-600"
            >
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${isSubmenuOpen ? 'rotate-180' : ''}`}
              />
            </button>
          )}
        </div>
        {/* Submenu */}
        {hasSubmenu && filteredSubmenu?.length > 0 && !effectivelyCollapsed && isSubmenuOpen && (
          <div className="pl-6 py-1 space-y-0.5">
            {filteredSubmenu.map(subItem => (
              <Link
                key={subItem.id}
                to={subItem.path}
                className={`flex items-center w-full px-2 py-1.5 rounded-lg text-sm transition-colors ${
                  location.pathname === subItem.path
                    ? "text-brand-600 font-bold bg-brand-50"
                    : "text-slate-600 hover:text-brand-600 hover:bg-slate-50"
                }`}
              >
                {subItem.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  const getRoleLabel = () => {
    // Dla adminów pokaż aktualny tryb widoku
    if (isAdmin) {
      if (viewMode === 'user') return 'User View';
      if (user?.role === 'superuser') return 'Superuser';
      return 'Admin';
    }
    if (user?.isCoordinator) return 'Coordinator';
    if (user?.inspector) return 'Inspector';
    return 'User';
  };

  return (
    <div
      className={`bg-white border-r border-slate-200 flex flex-col h-screen sticky top-0 transition-all duration-300 overflow-x-hidden ${
        effectivelyCollapsed ? "w-16" : "w-64"
      }`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Logo Header */}
      <div className="h-14 flex items-center justify-between px-3 border-b border-slate-100 overflow-hidden">
        {!effectivelyCollapsed ? (
          <div className="flex items-center gap-2 relative">
            {/* Ventro - Phase 0 */}
            <div className={`flex items-center gap-2 transition-all duration-700 ease-in-out ${
              logoPhase === 0 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-full absolute'
            }`}>
              <img src={logoSygnet} alt="Ventro" className="w-8 h-8 object-contain flex-shrink-0" />
              <span className="font-black text-slate-900 text-sm whitespace-nowrap">Ventro</span>
            </div>
            {/* Rebranding - Phase 1 */}
            <div className={`flex items-center gap-1 transition-all duration-700 ease-in-out ${
              logoPhase === 1 ? 'opacity-100 translate-x-0' : logoPhase === 0 ? 'opacity-0 translate-x-full absolute' : 'opacity-0 -translate-x-full absolute'
            }`}>
              <span className="font-bold text-slate-400 text-[10px] whitespace-nowrap">Rebranding</span>
              <span className="text-slate-400 text-xs">→</span>
              <img src={logoSygnet} alt="Entrigy" className="w-5 h-5 object-contain flex-shrink-0" />
              <span className="font-black text-brand-500 text-xs whitespace-nowrap">ENTRIGY</span>
            </div>
            {/* Entrigy - Phase 2 */}
            <div className={`flex items-center gap-2 transition-all duration-700 ease-in-out ${
              logoPhase === 2 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full absolute'
            }`}>
              <img src={logoSygnet} alt="Entrigy" className="w-8 h-8 object-contain flex-shrink-0" />
              <span className="text-brand-500 text-lg tracking-widest whitespace-nowrap" style={{ fontFamily: 'Arial Black, sans-serif', fontWeight: 900 }}>ENTRIGY</span>
            </div>
          </div>
        ) : (
          <img src={logoSygnet} alt="Logo" className="w-8 h-8 object-contain mx-auto" />
        )}
        {!effectivelyCollapsed && (
          <button
            onClick={toggleCollapse}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <svg className={`h-4 w-4 transition-transform ${effectivelyCollapsed ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-2 space-y-1 overflow-y-auto overflow-x-hidden" style={{ scrollbarWidth: 'thin', scrollbarColor: '#e2e8f0 transparent' }}>
        {subscriptionLoading ? (
          <div className="flex items-center justify-center p-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
            {!effectivelyCollapsed && <span className="ml-2 text-sm text-slate-600">Loading...</span>}
          </div>
        ) : (
          visibleMenuItems.map(item => renderMenuItem(item))
        )}
      </nav>

      {/* Footer */}
      <div className="p-2 mt-auto border-t border-slate-100">
        {/* Phone Book Button */}
        {onOpenPhoneBook && !effectivelyCollapsed && (
          <button
            onClick={onOpenPhoneBook}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors mb-1"
          >
            <Phone size={18} className="flex-shrink-0" />
            <span className="text-sm font-bold">Phone Book</span>
          </button>
        )}

        {/* User Menu */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => {
              setShowUserMenu(!showUserMenu);
              setSidebarCollapsed?.(false);
            }}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-600 hover:bg-slate-100 hover:text-slate-900 w-full transition-all ${
              effectivelyCollapsed ? "justify-center" : ""
            }`}
          >
            <div className="relative">
              {user?.profile_picture || user?.profilePicture ? (
                <img src={user.profile_picture || user.profilePicture} alt="Profile" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
              ) : (
                <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-black text-brand-600">
                    {user?.firstName?.charAt(0) || 'U'}
                  </span>
                </div>
              )}
              {notificationCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                  {notificationCount > 9 ? '9+' : notificationCount}
                </span>
              )}
            </div>
            {!effectivelyCollapsed && (
              <div className="text-left flex-1 min-w-0">
                <span className="text-sm font-bold text-slate-900 block truncate">
                  {user?.firstName ? `${user.firstName} ${user.lastName || ""}`.trim() : "Profile"}
                </span>
                <span className="text-[10px] font-bold text-brand-500 uppercase">{getRoleLabel()}</span>
              </div>
            )}
          </button>

          {showUserMenu && (
            <div className={`absolute ${effectivelyCollapsed ? "left-16" : "left-0"} bottom-full mb-2 w-72 bg-white rounded-xl shadow-lg border border-slate-200 z-50 overflow-hidden`}>
              {/* User info header */}
              <div className="p-3 border-b border-slate-100 flex items-center gap-3">
                {user?.profile_picture || user?.profilePicture ? (
                  <img src={user.profile_picture || user.profilePicture} alt="Profile" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-lg font-black text-brand-600">{user?.firstName?.charAt(0) || 'U'}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-900">{user?.firstName} {user?.lastName}</p>
                  <p className="text-[10px] text-slate-500 truncate">{user?.email}</p>
                </div>
              </div>

              {/* Notifications */}
              {notificationCount > 0 && (
                <div className="border-b border-slate-100">
                  <div className="px-3 py-2 flex items-center justify-between">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Notifications</span>
                    <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-2 py-0.5">{notificationCount}</span>
                  </div>
                  <div className="max-h-40 overflow-y-auto">
                    {ticketNotifications.slice(0, 3).map((notification) => (
                      <div
                        key={notification.ticket_id}
                        onClick={() => {
                          navigate('/settings', { state: { activeTab: 'support', selectedTicketId: notification.ticket_id } });
                          setShowUserMenu(false);
                        }}
                        className="px-3 py-2 hover:bg-slate-50 cursor-pointer transition-colors"
                      >
                        <div className="flex items-start gap-2">
                          <MessageCircle className="h-4 w-4 text-brand-500 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold text-slate-900 truncate">{notification.subject}</p>
                            <p className="text-[10px] text-slate-400">{new Date(notification.response_date).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Menu items */}
              <div className="p-1">
                <Link
                  to="/profile"
                  className="flex items-center gap-2 w-full px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                  onClick={() => setShowUserMenu(false)}
                >
                  <User size={14} />
                  My Profile
                </Link>
                <button
                  className="flex items-center gap-2 w-full px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                  onClick={() => {
                    navigate(`/powerpass/${user?.id}`);
                    setShowUserMenu(false);
                  }}
                >
                  <QrCode size={14} className="text-brand-500" />
                  View Powerpass
                </button>
                {/* Switch View Mode - tylko dla adminów */}
                {isAdmin && (
                  <button
                    className="flex items-center gap-2 w-full px-3 py-2 text-xs font-bold text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                    onClick={() => {
                      setViewMode(viewMode === 'admin' ? 'user' : 'admin');
                      setShowUserMenu(false);
                      // Przekieruj do odpowiedniego dashboardu
                      navigate('/dashboard');
                    }}
                  >
                    <RefreshCw size={14} />
                    Switch to {viewMode === 'admin' ? 'User' : 'Admin'} View
                  </button>
                )}
                <Link
                  to="/settings"
                  className="flex items-center gap-2 w-full px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                  onClick={() => setShowUserMenu(false)}
                >
                  <Settings size={14} />
                  Settings
                </Link>
                <Link
                  to="/changes"
                  className="flex items-center gap-2 w-full px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                  onClick={() => setShowUserMenu(false)}
                >
                  <FileText size={14} />
                  Changes List
                </Link>
                <button
                  className="flex items-center gap-2 w-full px-3 py-2 text-xs font-bold text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  onClick={handleLogout}
                >
                  <LogOut size={14} />
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnifiedSidebar;
